import { useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  alpha,
  styled,
} from '@mui/material';
import { motion, useInView } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { P} from '../theme/palette';
import { useTenantConfig } from '../hooks/useTenant';

// Section badge pill component
const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  borderRadius: 50,
  background: 'rgba(251, 144, 67, 0.08)',
  border: '1px solid rgba(249, 115, 22, 0.15)',
  marginBottom: 20,
});

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.1,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const INFRA_ITEMS = [
  {
    image: '/assets/icon/shield.webp',
    title: 'KVKK Uyumlu Veri Güvenliği',
    desc: 'Verileriniz şifrelenir, korunur ve mevzuata uygun saklanır.',
  },
  {
    image: '/assets/icon/turkcell-bulut.webp',
    title: 'Turkcell Bulut Altyapısı',
    desc: 'Yüksek performanslı, yedekli ve kesintisiz bulut sunucu mimarisi.',
    isLogo: true,
  },
  {
    image: '/assets/icon/stopwatch.webp',
    title: '%99.9 Uptime Garantisi',
    desc: 'Operasyonlarınız her zaman erişilebilir ve stabil.',
  },
  {
    image: '/assets/icon/folder.webp',
    title: 'Denetime Hazır Loglama',
    desc: 'Tüm işlemler kayıt altındadır. Şeffaf ve denetlenebilir sistem.',
  },
];

const TRUST_ITEMS = [
  'SLA destekli hizmet',
  'Yedekli veri merkezleri',
  '7/24 sistem izleme',
];

const SuperInfrastructureSection = () => {
  const { tenantName} = useTenantConfig();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 8, md: 12 },
        background: '#ffffff',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <SectionBadge>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#ffc403',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#ffc403',
                  letterSpacing: '0.5px',
                }}
              >
                SÜPER ALTYAPI
              </Typography>
            </SectionBadge>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.4rem', md: '3rem' },
                color: '#0f172ad1',
                mb: 2,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
              }}
            >
              Kurumsal Seviyede
              <br />
              Altyapı ve Güvenlik
            </Typography>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <Typography
              sx={{
                maxWidth: 600,
                mx: 'auto',
                color: P.neutral[500],
                fontSize: '1rem',
                lineHeight: 1.7,
              }}
            >
              {tenantName}, Turkcell bulut altyapısı üzerinde çalışır.
              Yüksek erişilebilirlik, veri güvenliği ve mevzuata uyumlu loglama
              ile dernek yönetimini güvenle yürütün.
            </Typography>
          </motion.div>
        </Box>

        {/* Cards */}
        <Grid container spacing={3}>
          {INFRA_ITEMS.map((item, i) => {
            return (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <motion.div
                  variants={fadeUp}
                  custom={i + 3}
                  initial="hidden"
                  animate={isInView ? 'visible' : 'hidden'}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      height: '100%',
                      background: '#ffffff',
                      border: `1px solid ${P.neutral[200]}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${alpha('#ffc403', 0.15)}`,
                        borderColor: alpha('#ffc403', 0.3),
                      },
                    }}
                  >
                    {item.isLogo ? (
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          mb: 2,
                          overflow: 'hidden',
                          borderRadius: 2.5,
                        }}
                      >
                        <img 
                          src={item.image} 
                          alt={item.title} 
                          style={{ 
                            height: 56,
                            width: 'auto',
                            maxWidth: 'none',
                          }} 
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, #ffc403 0%, #efb801ff 100%)`,
                          boxShadow: `0 8px 20px ${alpha('#ffc403', 0.25)}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2.5,
                        }}
                      >
                        <img 
                          src={item.image} 
                          alt={item.title} 
                          style={{ 
                            width: '60%', 
                            height: '60%', 
                            objectFit: 'contain',
                            filter: 'brightness(0) invert(1)' 
                          }} 
                        />
                      </Box>
                    )}

                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: P.neutral[800],
                        mb: 1,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: '0.85rem',
                        color: P.neutral[500],
                        lineHeight: 1.6,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom Trust Line */}
        <motion.div
          variants={fadeUp}
          custom={7}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <Box
            sx={{
              mt: { xs: 5, md: 7 },
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: { xs: 2, md: 4 },
            }}
          >
            {TRUST_ITEMS.map((item, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{
                    fontSize: 18,
                    color: '#eab403ff',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    color: P.neutral[600],
                    fontWeight: 500,
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default SuperInfrastructureSection;